import React, { useState } from 'react';
import { clsx } from 'clsx';
import CharacterDialogue from '../characters/CharacterDialogue';

// One key for every evening's answers: { [blockId]: optionIndex }. Kept so a
// reader who comes back to an evening finds the scene as they left it.
// "Reset all progress" on the Profile page clears it.
export const YOUR_TURN_KEY = 'fyvYourTurn';

const readAnswers = () => {
  try {
    return JSON.parse(localStorage.getItem(YOUR_TURN_KEY)) || {};
  } catch {
    return {};
  }
};

/**
 * A guide turns to the reader inside the scene, the reader answers with one
 * tap, and a guide answers what they chose. The assessment already works this
 * way; this brings the same device into the evenings, so the reader does
 * something in the story instead of only at the end of it.
 *
 * Block shape (src/data/modules/*.js):
 *   { type: 'yourTurn', id, character, speaker, prompt,
 *     options: [{ label, reply: { character, speaker, text } }] }
 */
const YourTurn = ({ id, characterId, speaker, prompt, options = [] }) => {
  const [choice, setChoice] = useState(() => {
    const saved = id ? readAnswers()[id] : undefined;
    return Number.isInteger(saved) && saved < options.length ? saved : null;
  });

  const choose = (index) => {
    setChoice(index);
    if (!id) return;
    try {
      localStorage.setItem(YOUR_TURN_KEY, JSON.stringify({ ...readAnswers(), [id]: index }));
    } catch {
      /* storage unavailable: the answer still shows for this visit */
    }
  };

  const reply = choice !== null ? options[choice]?.reply : null;
  const groupLabelId = `your-turn-${id || 'question'}`;

  return (
    <div className="my-8 rounded-2xl border-2 border-courtyard-gold/40 bg-white/70 p-5 sm:p-6 shadow-sm">
      <p className="text-xs font-bold uppercase tracking-wider text-courtyard-clay mb-3">
        Your turn
      </p>

      <div id={groupLabelId}>
        <CharacterDialogue characterId={characterId} speaker={speaker} text={prompt} />
      </div>

      <div role="group" aria-labelledby={groupLabelId} className="mt-4 grid gap-2">
        {options.map((option, index) => (
          <button
            key={option.label}
            type="button"
            aria-pressed={choice === index}
            onClick={() => choose(index)}
            className={clsx(
              'w-full text-left rounded-xl border px-4 py-3 text-[15px] leading-relaxed transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-courtyard-clay',
              choice === index
                ? 'border-courtyard-clay bg-courtyard-clay/10 text-courtyard-stone font-semibold'
                : 'border-courtyard-stone/20 bg-white text-courtyard-stone hover:border-courtyard-clay/60 hover:bg-courtyard-gold/5',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div aria-live="polite" className="mt-4">
        {reply && (
          <CharacterDialogue
            characterId={reply.character}
            speaker={reply.speaker}
            text={reply.text}
          />
        )}
      </div>
    </div>
  );
};

export default YourTurn;
